import { Box, Button, Grid } from "@mui/material";
import { useState } from "react";

import { set } from "lodash";
import useAsyncEffect from "use-async-effect";
import { HistoryProps } from "../..";
import { medicalsApi } from "../../../../../../api/medicalsApi";
import ComponentLoader from "../../../../../../components/ComponentLoader";
import { NotificationVariant } from "../../../../../../components/dashboard/NotificationsProvider";
import NotesInput from "../../../../../../components/form/NotesInput";
import useMedicalEncounter from "../../../../../../hooks/useMedicalEncounter";
import useNotifications from "../../../../../../hooks/useNotifications";
import usePractitioner from "../../../../../../hooks/usePractitioner";
import {
  createEncounterParam,
  createReference,
} from "../../../../../../utils/DataTransformationUtils";
import { FHIRURL } from "../../../../../../utils/FHIRURL";
import {
  QuestionnaireItem,
  QuestionnaireItemTypes,
  createFlattendResponses,
  createQuestionnaireItemPath,
  createResponseItem,
  enableQuestion,
} from "../../../components/QuestionnaireItemTypes";
import { emptyHistoryItems } from "../HistoryHelpers";
import { useDictationCheck } from "../../../../../../hooks/useDictationCheck";

const ENTITY_TYPE = "questionnaire";
const SH_QUESTIONNAIRE_CODE = "371828001";

export default function SexualHistoryForm({
  item,
  patientReference: _patientReference,
  disabled,
  setSaved,
  patientId,
}: HistoryProps) {
  const {
    currentPatient,
    encounterReference,
    patientReference,
    setEncounterToInProgress,
    refreshPatientHistory,
  } = useMedicalEncounter();
  const { isDictationActive } = useDictationCheck();

  const practitionerReference = usePractitioner();

  const [loading, setLoading] = useState(false);
  const [questionnaire, setQuestionnaire] = useState<any>();
  const [flattenedAnswers, setFlattenedAnswers] = useState({});
  const [questionnaireResponse, setQuestionnaireResponse] = useState<any>({
    ...emptyHistoryItems.sexual(patientReference),
  });

  const { setNotification } = useNotifications();

  useAsyncEffect(async () => {
    try {
      if (patientId || patientReference) {
        setLoading(true);

        const _response = await medicalsApi.findQuestionnaireResponse(
          SH_QUESTIONNAIRE_CODE,
          {
            subject: patientReference ? patientReference.identifier : patientId,
            e: encounterReference?.identifier,
            "exclude-status": "entered-in-error",
          },
        );

        if (!Array.isArray(_response) || _response.length > 1) {
          throw new Error("Not array or array length more than 1");
        }

        let response;
        if (_response.length === 1) {
          response = _response[0];
        }

        if (response) {
          setQuestionnaireResponse(response);
          setFlattenedAnswers(createFlattendResponses(response.item || []));
        }

        let _questionnaire: any;
        if (response?.questionnaire) {
          const fhirUrl = new FHIRURL(response.questionnaire);
          _questionnaire = await medicalsApi.findEntities(
            {
              id: fhirUrl.id,
              version: fhirUrl.searchParams.get("version"),
              code: fhirUrl.searchParams.get("code"),
            },
            "questionnaire",
          );
        } else {
          _questionnaire = await medicalsApi.findEntities(
            { code: SH_QUESTIONNAIRE_CODE, status: "active" },
            ENTITY_TYPE,
          );
        }

        if (!_questionnaire || _questionnaire.length === 0) {
          throw new Error(
            "Sexual history questionnaire is either more than or less than 1",
          );
        }

        setQuestionnaire(_questionnaire[0]);
      }
    } catch (error) {
      console.error(error);
      setNotification({
        message:
          "Unable to retreive sexual history questionnaire. Please try again later.",
        variant: NotificationVariant.error,
      });
    } finally {
      setLoading(false);
    }
  }, [patientId, patientReference]);
  const cleanEnableWhen = (disabledQuestion: any) => {
    const indices = disabledQuestion.linkId.split(".");

    const path: any = createQuestionnaireItemPath(indices);
    const _questionnaireResponse = { ...questionnaireResponse };

    set(_questionnaireResponse, path, {
      linkId: disabledQuestion.linkId,
      text: disabledQuestion.text,
    });
  };
  const onItemChange = (item: QuestionnaireItem, value: any) => {
    const indices = item.linkId.split(".");
    const path: any = createQuestionnaireItemPath(indices);
    const _questionnaireResponse = { ...questionnaireResponse };
    set(_questionnaireResponse, path, createResponseItem(item, value));
    setQuestionnaireResponse(_questionnaireResponse);

    const flattened = createFlattendResponses(
      _questionnaireResponse.item || [],
    );
    setFlattenedAnswers(flattened);
    setSaved(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...questionnaireResponse,
        questionnaire: new FHIRURL(`Questionnaire/${questionnaire.id}`, {
          version: questionnaire.version,
          code: questionnaire.code[0]?.code,
        }).url,
        authored: new Date(),
        author: practitionerReference,
        source: practitionerReference,
        subject: patientReference ?? createReference("Patient", patientId),
      };

      if (encounterReference) {
        data.encounter = encounterReference;
      }

      if (!data.meta) {
        // In this case, create a new questionnaire response
        setQuestionnaireResponse(
          await medicalsApi.createEntity(
            { ...data, status: "completed" },
            "questionnaire-response",
            createEncounterParam(encounterReference),
          ),
        );
      } else {
        // Update existing questionnaire
        setQuestionnaireResponse(
          await medicalsApi.updateEntity(
            data.id,
            { ...data, status: "amended" },
            "questionnaire-response",
            createEncounterParam(encounterReference),
          ),
        );
      }

      // can be changed to await
      setEncounterToInProgress();

      setSaved(true);
      refreshPatientHistory();
      setNotification({
        variant: NotificationVariant.success,
        message: "Successfully saved the patient's sexual history.",
      });
    } catch (error) {
      console.error(error);
      setNotification({
        variant: NotificationVariant.error,
        message:
          "An error occured while saving patient's sexual history. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit}>
      {questionnaire && (
        <Grid container spacing={3} xs={12}>
          {questionnaire.item?.map((item: QuestionnaireItem, i) => {
            item.item.forEach((question) => {
              if (!enableQuestion(question, flattenedAnswers, onItemChange)) {
                cleanEnableWhen(question);
              }
            });
            const QuestionnaireComponent = item.type ? (
              QuestionnaireItemTypes[item.type]
            ) : (
              <></>
            );
            return (
              <>
                {enableQuestion(item, flattenedAnswers, onItemChange) && (
                  <Grid item xs={12} key={`question-${i}`}>
                    <QuestionnaireComponent
                      item={item}
                      onChange={onItemChange}
                      value={flattenedAnswers[item?.linkId]}
                      answers={flattenedAnswers}
                      disabled={disabled}
                      formula={item.formula}
                      sex={currentPatient.gender}
                    />
                  </Grid>
                )}
              </>
            );
          })}
        </Grid>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
      >
        <Box flex={1}>
          <NotesInput
            disabled={disabled}
            value={questionnaireResponse?.note || []}
            label="Comments/Notes"
            onChange={(note) => {
              setQuestionnaireResponse({ ...questionnaireResponse, note });
              setSaved(false);
            }}
            placeholderText="Enter comments/notes about the patient's gynaecological history"
          />
        </Box>
        <Box sx={{ mt: 2, alignSelf: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={disabled || loading || isDictationActive}
            sx={{
              mr: 3,
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
      <ComponentLoader loading={loading} />
    </form>
  );
}
